import type { ErrorComponentProps } from "@tanstack/react-router";
import { ErrorState } from "@strust/ui";
import { css } from "@strust/ui/css";
import { useIntl } from "react-intl";
import { routeErrorMessages } from "./i18n/messages.ts";
import { isChunkLoadError } from "./stale-chunk.ts";

export interface RouteErrorProps {
  /** The screen's code is from an older build than the one published (a deploy happened). */
  readonly staleBuild: boolean;
  readonly onReload: () => void;
}

/** Screen shown when a route fails to load or render, with a reload action. */
export function RouteError({ staleBuild, onReload }: RouteErrorProps) {
  const intl = useIntl();
  return (
    <main
      id="main-content"
      className={css({
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6",
        bg: "bg",
      })}
    >
      <ErrorState
        className={css({ maxWidth: "md", width: "full" })}
        title={intl.formatMessage(
          staleBuild ? routeErrorMessages.newVersionTitle : routeErrorMessages.title,
        )}
        retryLabel={intl.formatMessage(routeErrorMessages.reload)}
        onRetry={onReload}
      >
        {intl.formatMessage(
          staleBuild ? routeErrorMessages.newVersionBody : routeErrorMessages.body,
        )}
      </ErrorState>
    </main>
  );
}

function reloadPage() {
  window.location.reload();
}

/**
 * The router's default error component. A failed chunk load (a tab opened before a deploy) says a
 * new version is available; the reload itself is left to the `vite:preloadError` recovery
 * (`installStaleChunkRecovery`), which waits a moment so that a chunk cancelled by the user leaving
 * the page never reloads it, and reloads at most once per window. Any other error shows the
 * generic screen. Both offer the Reload action.
 */
export function DefaultRouteError({ error }: ErrorComponentProps) {
  return <RouteError staleBuild={isChunkLoadError(error)} onReload={reloadPage} />;
}
